import { create } from 'zustand';
import type { Card, CollectionDeck, DeckEntry } from '@mtg-deck-tool/shared';
import type {
  CardInstance,
  GameZone,
  HordeDeckData,
  HordeState,
  HordeZone,
  PlaytesterPanel,
  PlaytesterState,
  PlaytesterZoom,
  PlayerCounters,
} from '@mtg-deck-tool/shared';
import { GENERIC_COUNTER_KEY, parsePrintedPTValue } from '@mtg-deck-tool/shared';
import {
  buildInitialZones,
  cardDimensions,
  createInstance,
  drawFromLibrary,
  flipCoin,
  hordeReveal,
  roll2D6,
  rollD20,
  rollD6,
  shuffleInstances,
} from '../lib/playtester-engine.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Remove an instance from a zone array by instanceId.
 */
function removeFromZone(instances: CardInstance[], instanceId: string): CardInstance[] {
  return instances.filter((i) => i.instanceId !== instanceId);
}

/**
 * Update a single instance within a zone array.
 */
function updateInstance(
  instances: CardInstance[],
  instanceId: string,
  updater: (i: CardInstance) => CardInstance,
): CardInstance[] {
  return instances.map((i) => (i.instanceId === instanceId ? updater(i) : i));
}

/**
 * Build the state for restarting a horde session with the SAME parameters:
 * the same horde deck reshuffled to the same size, the same player deck, and
 * the official solo setup (30 life, three setup turns).
 *
 * Shared by restart() and the win/lose overlay's "New Game" so both behave
 * identically. Returns null when no horde session is active, in which case the
 * caller falls back to a normal goldfish restart.
 */
function buildHordeRestart(state: PlaytesterState): Partial<PlaytesterState> | null {
  if (!state.horde) return null;

  // Everything the horde was dealt, reshuffled. Player-created tokens placed on
  // the horde battlefield are NOT deck cards and cease to exist (rule 111.7);
  // the horde's own token CARDS have isToken false and are kept.
  const hordeLibrary = shuffleInstances(
    [
      ...state.horde.library,
      ...state.horde.battlefield,
      ...state.horde.graveyard,
      ...state.horde.revealPanel,
    ].filter((i) => !i.isToken),
  ).slice(0, state.horde.deckSize);

  const allPlayerInstances = [
    ...state.library,
    ...state.hand,
    ...state.battlefield,
    ...state.graveyard,
    ...state.exile,
    ...state.command,
  ];
  const command = allPlayerInstances
    .filter((i) => i.isCommander)
    .map((i) => createInstance(i.card, true));
  const libraryInstances = shuffleInstances(
    allPlayerInstances
      .filter((i) => !i.isCommander && !i.isToken)
      .map((i) => createInstance(i.card, false)),
  );
  const { hand, library } = drawFromLibrary(libraryInstances, [], 7);

  return {
    lifeTotal: 30,
    turnNumber: 1,
    playerCounters: { poison: 0, energy: 0, experience: 0 },
    command,
    library,
    hand,
    battlefield: [],
    graveyard: [],
    exile: [],

    // Transient UI state — a restart is a clean slate
    selectedInstanceIds: new Set<string>(),
    openPanel: null,
    contextMenu: null,
    showCounterModal: false,
    showPTModal: false,
    overlay: null,
    persistentPreviewCard: null,
    hoveredCard: null,
    hoveredInstanceId: null,
    hoveredZone: null,
    diceResult: null,
    diceResultExpiresAt: null,
    scryCount: 0,
    scryCards: [],
    scryBottom: new Set<string>(),
    showTopCard: false,

    // Same horde deck, same size — only the game state resets
    horde: {
      ...state.horde,
      phase: 'setup' as const,
      setupTurn: 1,
      library: hordeLibrary,
      battlefield: [],
      graveyard: [],
      revealPanel: [],
    },
  };
}

// ── Zoom persistence ──────────────────────────────────────────────────────────

const ZOOM_STORAGE_KEY = 'mtg-pt-zoom-v2';
const ZOOM_MIN = 50;
const ZOOM_MAX = 200;
const ZOOM_DEFAULT = 150;

function getStoredZoom(): number {
  try {
    const raw = localStorage.getItem(ZOOM_STORAGE_KEY);
    if (raw === null) return ZOOM_DEFAULT;
    const n = parseInt(raw, 10);
    if (isNaN(n) || n < ZOOM_MIN || n > ZOOM_MAX) return ZOOM_DEFAULT;
    return n;
  } catch {
    return ZOOM_DEFAULT;
  }
}

function persistZoom(z: number): void {
  try { localStorage.setItem(ZOOM_STORAGE_KEY, String(z)); } catch { /* SSR/sandbox */ }
}

// ── Initial state ─────────────────────────────────────────────────────────────

const INITIAL_STATE: PlaytesterState = {
  isActive: false,
  deckId: '',
  deckName: '',

  library: [],
  hand: [],
  battlefield: [],
  graveyard: [],
  exile: [],
  command: [],

  lifeTotal: 40,
  turnNumber: 1,
  playerCounters: { poison: 0, energy: 0, experience: 0 },

  selectedInstanceIds: new Set<string>(),

  zoom: ZOOM_DEFAULT,
  isClickToTapMode: false,
  showTopCard: false,
  hoveredCard: null,
  hoveredInstanceId: null,
  hoveredZone: null,
  persistentPreviewCard: null,
  diceResult: null,
  diceResultExpiresAt: null,

  openPanel: null,
  scryCount: 0,
  scryCards: [],
  scryBottom: new Set<string>(),

  millInput: 1,

  deckTokens: null,
  tokenSearchQuery: '',
  tokenSearchResults: [],
  isTokenSearching: false,

  contextMenu: null,

  showCounterModal: false,

  showPTModal: false,

  overlay: null,

  horde: null,
};

// ── Action types ──────────────────────────────────────────────────────────────

interface PlaytesterActions {
  // ── Lifecycle ──────────────────────────────────────────────────────────
  initializeGame(deck: CollectionDeck | { name: string; id: string; entries: DeckEntry[] }, source: 'collection' | 'editor'): void;
  exitGame(): void;
  restart(): void;

  // ── Zone moves ──────────────────────────────────────────────────────────
  moveToZone(instanceIds: string[], fromZone: GameZone, toZone: GameZone): void;
  moveToTopOfLibrary(instanceIds: string[]): void;
  moveToBottomOfLibrary(instanceIds: string[]): void;
  moveToLibraryPosition(instanceId: string, pos: number): void;

  // ── Battlefield layout ──────────────────────────────────────────────────
  /** Move a battlefield card to an absolute position (free-form placement). */
  setCardPosition(instanceId: string, x: number, y: number): void;

  // ── Card state ──────────────────────────────────────────────────────────
  tapCard(instanceId: string): void;
  tapCards(instanceIds: string[]): void;
  untapAll(): void;
  flipCard(instanceId: string): void;
  setDoesntUntap(instanceId: string, value: boolean): void;
  addCounter(instanceId: string, name: string, delta: number): void;
  setCounter(instanceId: string, name: string, value: number): void;
  removeCounter(instanceId: string, name: string): void;
  /** Add 1 to the generic counter (bottom-left badge). Works on player and horde cards. */
  addGenericCounter(instanceId: string): void;
  /** Remove 1 from the generic counter, floored at 0 (removes the key when it would go below 1). */
  removeGenericCounter(instanceId: string): void;
  /** Clear ALL counters (both generic and named) from the card. */
  resetCounters(instanceId: string): void;
  /**
   * Adjust power and toughness by deltas. If no override is set yet, seeds from
   * the card's printed values (non-numeric printed values like "*" seed from 0).
   */
  adjustPowerToughness(instanceId: string, dPower: number, dToughness: number): void;
  /** Set absolute power and toughness override values. */
  setPowerToughness(instanceId: string, power: number, toughness: number): void;
  /** Clear the P/T override so the printed value is shown again. */
  resetPowerToughness(instanceId: string): void;

  // ── Player stats ────────────────────────────────────────────────────────
  adjustLife(delta: number): void;
  setLife(value: number): void;
  setTurnNumber(value: number): void;
  nextTurn(): void;
  shuffleLibrary(): void;
  drawCard(): void;
  millTopX(n: number): void;
  adjustPlayerCounter(type: keyof PlayerCounters, delta: number): void;

  // ── Commander ───────────────────────────────────────────────────────────
  castCommander(instanceId: string): void;
  returnToCommandZone(instanceId: string): void;

  // ── Token ───────────────────────────────────────────────────────────────
  /**
   * Copy a card as a token onto the battlefield, cascading down-right so
   * repeated copies form a readable ladder rather than a single stack.
   */
  makeTokenCopy(instanceId: string): void;
  /** Place a player-created token on the player battlefield. Optional x/y places it near a source card. */
  placeToken(card: Card, x?: number, y?: number): void;
  /** Place a player-created token on the HORDE battlefield. Ceases to exist when it leaves (rule 111.7). */
  placeHordeToken(card: Card, x?: number, y?: number): void;
  setDeckTokens(tokens: Card[]): void;
  setTokenSearchResults(cards: Card[], query: string): void;

  // ── Scry ────────────────────────────────────────────────────────────────
  openScry(count: number): void;
  scryReorder(fromIndex: number, toIndex: number): void;
  scryToggleBottom(instanceId: string): void;
  scryConfirm(): void;

  // ── Selection ───────────────────────────────────────────────────────────
  setSelection(ids: string[]): void;
  toggleSelection(id: string): void;
  clearSelection(): void;

  // ── UI ──────────────────────────────────────────────────────────────────
  setZoom(z: number): void;
  adjustZoom(delta: number): void;
  toggleClickToTap(): void;
  toggleShowTopCard(): void;
  setHoveredCard(card: Card | null): void;
  setHoveredInstanceId(id: string | null): void;
  setHoveredZone(zone: GameZone | null): void;
  setPersistentPreview(card: Card | null): void;
  setOpenPanel(p: PlaytesterPanel): void;
  setMillInput(n: number): void;
  setContextMenu(m: { x: number; y: number; instanceId: string; zone: GameZone } | null): void;
  openCounterModal(): void;
  closeCounterModal(): void;
  openPTModal(): void;
  closePTModal(): void;
  rollDice(type: 'coin' | 'd6' | '2d6' | 'd20'): void;

  // ── Phase 2: Horde ──────────────────────────────────────────────────────────
  initHorde(data: HordeDeckData, resolvedCards: Card[], size: 25 | 50 | 75 | 100): void;
  endSetupTurn(): void;
  endPlayerTurn(): void;
  beginMyTurn(): void;
  hordeMillCards(n: number): void;
  checkHordeWinCondition(): void;
  dismissOverlay(mode: 'continue' | 'return' | 'new-game'): void;
  moveHordeCardToZone(instanceId: string, fromZone: HordeZone, toZone: HordeZone): void;
  setHordeMoveResult(instanceId: string, toZone: HordeZone): void;
  setHordeMillInput(n: number): void;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export type PlaytesterStore = PlaytesterState & PlaytesterActions;

export const usePlaytesterStore = create<PlaytesterStore>()((set, get) => ({
  ...INITIAL_STATE,
  // zoom is persistent — seed from localStorage, default 140
  zoom: getStoredZoom(),
  selectedInstanceIds: new Set<string>(),
  scryBottom: new Set<string>(),

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  initializeGame: (deck: CollectionDeck | { name: string; id: string; entries: DeckEntry[] }, _source: 'collection' | 'editor') => {
    const entries = deck.entries;
    const { command, library } = buildInitialZones(entries);
    const { hand, library: lib } = drawFromLibrary(library, [], 7);
    set({
      ...INITIAL_STATE,
      zoom: get().zoom,            // zoom is a persistent UI preference — never reset
      selectedInstanceIds: new Set<string>(),
      scryBottom: new Set<string>(),
      isActive: true,
      deckId: deck.id,
      deckName: deck.name,
      command,
      library: lib,
      hand,
      lifeTotal: 40,
      turnNumber: 1,
    });
  },

  exitGame: () => {
    set({
      ...INITIAL_STATE,
      zoom: get().zoom,            // zoom is a persistent UI preference — never reset
      selectedInstanceIds: new Set<string>(),
      scryBottom: new Set<string>(),
    });
  },

  restart: () => {
    const state = get();

    // In a horde game, Restart restarts THAT game with the same parameters —
    // same horde deck, same size, 30 life, three setup turns — rather than
    // dropping back to a goldfish game.
    const hordeRestart = buildHordeRestart(state);
    if (hordeRestart) {
      set(hordeRestart);
      return;
    }

    // Reconstruct game from all zones since we don't persist original entries
    const allInstances = [
      ...state.library,
      ...state.hand,
      ...state.battlefield,
      ...state.graveyard,
      ...state.exile,
      ...state.command,
    ];

    const commandIds = new Set(state.command.map((i) => i.instanceId));
    const commanderCards = state.command.map((i) => i.card);
    const nonCommanderCards = allInstances
      .filter((i) => !commandIds.has(i.instanceId) && !i.isToken)  // tokens cease to exist
      .map((i) => i.card);

    const commandInstances = commanderCards.map((c) => createInstance(c, true));
    const libraryInstances = shuffleInstances(nonCommanderCards.map((c) => createInstance(c, false)));
    const { hand, library } = drawFromLibrary(libraryInstances, [], 7);

    set({
      ...INITIAL_STATE,
      zoom: state.zoom,            // zoom is a persistent UI preference — never reset
      selectedInstanceIds: new Set<string>(),
      scryBottom: new Set<string>(),
      isActive: true,
      deckId: state.deckId,
      deckName: state.deckName,
      command: commandInstances,
      library,
      hand,
      lifeTotal: 40,
      turnNumber: 1,
    });
  },

  // ── Zone moves ─────────────────────────────────────────────────────────────

  moveToZone: (instanceIds: string[], fromZone: GameZone, toZone: GameZone) => {
    set((state) => {
      const sourceCards: CardInstance[] = [];
      let fromArr = [...state[fromZone]];
      for (const id of instanceIds) {
        const card = fromArr.find((i) => i.instanceId === id);
        if (card) {
          sourceCards.push(card);
          fromArr = fromArr.filter((i) => i.instanceId !== id);
        }
      }

      // Magic rule 111.7 — player tokens cease to exist the moment they leave
      // the battlefield (they are removed from the source zone above but NOT
      // added to any destination zone).
      const cardsToPlace = fromZone === 'battlefield'
        ? sourceCards.filter((c) => !c.isToken)
        : sourceCards;

      // Assign default grid positions for cards arriving on the battlefield
      // (caller may override via setCardPosition after drag-to-exact-position)
      let placed = cardsToPlace;
      if (toZone === 'battlefield') {
        const { zoom } = state;
        const { cardW, cardH } = cardDimensions(zoom);
        const gap = 6;
        const cols = 8;
        const baseIdx = state.battlefield.length;
        placed = cardsToPlace.map((c, i) => {
          if (c.x !== undefined) return c; // already positioned (e.g. drag-to-exact)
          const idx = baseIdx + i;
          return { ...c, x: (idx % cols) * (cardW + gap), y: Math.floor(idx / cols) * (cardH + gap) };
        });
      }
      // Library drop: place card ON TOP (index 0) per item #5
      const toArr = toZone === 'library'
        ? [...placed, ...state[toZone]]
        : [...state[toZone], ...placed];
      return { [fromZone]: fromArr, [toZone]: toArr } as Partial<PlaytesterStore>;
    });
  },

  setCardPosition: (instanceId: string, x: number, y: number) => {
    set((state) => ({
      battlefield: state.battlefield.map((i) =>
        i.instanceId === instanceId ? { ...i, x, y } : i,
      ),
    }));
  },

  moveToTopOfLibrary: (instanceIds: string[]) => {
    set((state) => {
      const movedCards: CardInstance[] = [];
      let newHand = [...state.hand];
      let newBattlefield = [...state.battlefield];
      let newGraveyard = [...state.graveyard];
      let newExile = [...state.exile];
      let newCommand = [...state.command];

      for (const id of instanceIds) {
        for (const [arr, setter] of [
          [newHand, (a: CardInstance[]) => { newHand = a; }] as const,
          [newBattlefield, (a: CardInstance[]) => { newBattlefield = a; }] as const,
          [newGraveyard, (a: CardInstance[]) => { newGraveyard = a; }] as const,
          [newExile, (a: CardInstance[]) => { newExile = a; }] as const,
          [newCommand, (a: CardInstance[]) => { newCommand = a; }] as const,
        ]) {
          const found = arr.find((i) => i.instanceId === id);
          if (found) {
            setter(arr.filter((i) => i.instanceId !== id));
            movedCards.push(found);
            break;
          }
        }
      }

      // Tokens that were on the battlefield cease to exist (Magic rule 111.7)
      const nonTokenMoved = movedCards.filter((c) => !c.isToken);
      return {
        library: [...nonTokenMoved, ...state.library],
        hand: newHand,
        battlefield: newBattlefield,
        graveyard: newGraveyard,
        exile: newExile,
        command: newCommand,
      };
    });
  },

  moveToBottomOfLibrary: (instanceIds: string[]) => {
    set((state) => {
      const movedCards: CardInstance[] = [];
      let newHand = [...state.hand];
      let newBattlefield = [...state.battlefield];
      let newGraveyard = [...state.graveyard];
      let newExile = [...state.exile];
      let newCommand = [...state.command];

      for (const id of instanceIds) {
        for (const [arr, setter] of [
          [newHand, (a: CardInstance[]) => { newHand = a; }] as const,
          [newBattlefield, (a: CardInstance[]) => { newBattlefield = a; }] as const,
          [newGraveyard, (a: CardInstance[]) => { newGraveyard = a; }] as const,
          [newExile, (a: CardInstance[]) => { newExile = a; }] as const,
          [newCommand, (a: CardInstance[]) => { newCommand = a; }] as const,
        ]) {
          const found = arr.find((i) => i.instanceId === id);
          if (found) {
            setter(arr.filter((i) => i.instanceId !== id));
            movedCards.push(found);
            break;
          }
        }
      }

      const nonTokenMoved = movedCards.filter((c) => !c.isToken);
      return {
        library: [...state.library, ...nonTokenMoved],
        hand: newHand,
        battlefield: newBattlefield,
        graveyard: newGraveyard,
        exile: newExile,
        command: newCommand,
      };
    });
  },

  moveToLibraryPosition: (instanceId: string, pos: number) => {
    set((state) => {
      let found: CardInstance | undefined;
      const zones: GameZone[] = ['hand', 'battlefield', 'graveyard', 'exile', 'command'];
      const updates: Partial<PlaytesterState> = {};
      for (const zone of zones) {
        const arr = state[zone];
        const idx = arr.findIndex((i) => i.instanceId === instanceId);
        if (idx !== -1) {
          found = arr[idx];
          updates[zone] = arr.filter((i) => i.instanceId !== instanceId) as CardInstance[];
          break;
        }
      }
      if (!found) return {};
      // Tokens cease to exist when leaving the battlefield (Magic rule 111.7)
      if (found.isToken) return updates as Partial<PlaytesterStore>;

      const lib = [...state.library];
      const insertPos = Math.max(0, Math.min(pos, lib.length));
      lib.splice(insertPos, 0, found);
      return { ...updates, library: lib };
    });
  },

  // ── Card state ─────────────────────────────────────────────────────────────

  tapCard: (instanceId: string) => {
    set((state) => ({
      battlefield: updateInstance(state.battlefield, instanceId, (i) => ({
        ...i,
        tapped: !i.tapped,
      })),
    }));
  },

  tapCards: (instanceIds: string[]) => {
    set((state) => ({
      battlefield: state.battlefield.map((i) =>
        instanceIds.includes(i.instanceId) ? { ...i, tapped: !i.tapped } : i
      ),
    }));
  },

  untapAll: () => {
    set((state) => ({
      battlefield: state.battlefield.map((i) =>
        i.doesntUntap ? i : { ...i, tapped: false }
      ),
    }));
  },

  flipCard: (instanceId: string) => {
    set((state) => {
      const zones: GameZone[] = ['hand', 'battlefield', 'graveyard', 'exile', 'command'];
      const updates: Partial<PlaytesterState> = {};
      for (const zone of zones) {
        if (state[zone].some((i) => i.instanceId === instanceId)) {
          updates[zone] = updateInstance(state[zone], instanceId, (i) => ({
            ...i,
            faceDown: !i.faceDown,
          })) as CardInstance[];
        }
      }
      return updates;
    });
  },

  setDoesntUntap: (instanceId: string, value: boolean) => {
    set((state) => ({
      battlefield: updateInstance(state.battlefield, instanceId, (i) => ({
        ...i,
        doesntUntap: value,
      })),
    }));
  },

  addCounter: (instanceId: string, name: string, delta: number) => {
    set((state) => {
      const zones: GameZone[] = ['battlefield', 'command'];
      const updates: Partial<PlaytesterState> = {};
      for (const zone of zones) {
        if (state[zone].some((i) => i.instanceId === instanceId)) {
          updates[zone] = updateInstance(state[zone], instanceId, (i) => {
            const current = i.counters[name] ?? 0;
            const next = current + delta;
            const counters = { ...i.counters };
            if (next <= 0) {
              delete counters[name];
            } else {
              counters[name] = next;
            }
            return { ...i, counters };
          }) as CardInstance[];
        }
      }
      // Also support horde battlefield
      if (state.horde?.battlefield.some((i) => i.instanceId === instanceId)) {
        const updatedHordeBF = updateInstance(state.horde.battlefield, instanceId, (i) => {
          const current = i.counters[name] ?? 0;
          const next = current + delta;
          const counters = { ...i.counters };
          if (next <= 0) {
            delete counters[name];
          } else {
            counters[name] = next;
          }
          return { ...i, counters };
        }) as CardInstance[];
        updates.horde = { ...state.horde, battlefield: updatedHordeBF };
      }
      return updates;
    });
  },

  setCounter: (instanceId: string, name: string, value: number) => {
    set((state) => {
      const zones: GameZone[] = ['battlefield', 'command'];
      const updates: Partial<PlaytesterState> = {};
      for (const zone of zones) {
        if (state[zone].some((i) => i.instanceId === instanceId)) {
          updates[zone] = updateInstance(state[zone], instanceId, (i) => {
            const counters = { ...i.counters };
            if (value <= 0) {
              delete counters[name];
            } else {
              counters[name] = value;
            }
            return { ...i, counters };
          }) as CardInstance[];
        }
      }
      // Also support horde battlefield
      if (state.horde?.battlefield.some((i) => i.instanceId === instanceId)) {
        const updatedHordeBF = updateInstance(state.horde.battlefield, instanceId, (i) => {
          const counters = { ...i.counters };
          if (value <= 0) {
            delete counters[name];
          } else {
            counters[name] = value;
          }
          return { ...i, counters };
        }) as CardInstance[];
        updates.horde = { ...state.horde, battlefield: updatedHordeBF };
      }
      return updates;
    });
  },

  removeCounter: (instanceId: string, name: string) => {
    set((state) => {
      const zones: GameZone[] = ['battlefield', 'command'];
      const updates: Partial<PlaytesterState> = {};
      for (const zone of zones) {
        if (state[zone].some((i) => i.instanceId === instanceId)) {
          updates[zone] = updateInstance(state[zone], instanceId, (i) => {
            const counters = { ...i.counters };
            delete counters[name];
            return { ...i, counters };
          }) as CardInstance[];
        }
      }
      // Also support horde battlefield
      if (state.horde?.battlefield.some((i) => i.instanceId === instanceId)) {
        const updatedHordeBF = updateInstance(state.horde.battlefield, instanceId, (i) => {
          const counters = { ...i.counters };
          delete counters[name];
          return { ...i, counters };
        }) as CardInstance[];
        updates.horde = { ...state.horde, battlefield: updatedHordeBF };
      }
      return updates;
    });
  },

  addGenericCounter: (instanceId: string) => {
    get().addCounter(instanceId, GENERIC_COUNTER_KEY, 1);
  },

  removeGenericCounter: (instanceId: string) => {
    // addCounter already floors at 0 (deletes key when next <= 0)
    get().addCounter(instanceId, GENERIC_COUNTER_KEY, -1);
  },

  resetCounters: (instanceId: string) => {
    set((state) => {
      const zones: GameZone[] = ['battlefield', 'command'];
      const updates: Partial<PlaytesterState> = {};
      for (const zone of zones) {
        if (state[zone].some((i) => i.instanceId === instanceId)) {
          updates[zone] = updateInstance(state[zone], instanceId, (i) => ({
            ...i,
            counters: {},
          })) as CardInstance[];
        }
      }
      // Also support horde battlefield
      if (state.horde?.battlefield.some((i) => i.instanceId === instanceId)) {
        const updatedHordeBF = updateInstance(state.horde.battlefield, instanceId, (i) => ({
          ...i,
          counters: {},
        })) as CardInstance[];
        updates.horde = { ...state.horde, battlefield: updatedHordeBF };
      }
      return updates;
    });
  },

  adjustPowerToughness: (instanceId: string, dPower: number, dToughness: number) => {
    set((state) => {
      const allZones: Array<{ zone: GameZone; arr: CardInstance[] }> = [
        { zone: 'battlefield', arr: state.battlefield },
        { zone: 'command', arr: state.command },
      ];
      const updates: Partial<PlaytesterState> = {};

      for (const { zone, arr } of allZones) {
        const instance = arr.find((i) => i.instanceId === instanceId);
        if (instance) {
          const seedPower = instance.power ?? parsePrintedPTValue(instance.card.power ?? instance.card.cardFaces?.[0]?.power);
          const seedToughness = instance.toughness ?? parsePrintedPTValue(instance.card.toughness ?? instance.card.cardFaces?.[0]?.toughness);
          updates[zone] = updateInstance(arr, instanceId, (i) => ({
            ...i,
            power: seedPower + dPower,
            toughness: seedToughness + dToughness,
          })) as CardInstance[];
        }
      }

      // Also support horde battlefield
      const hordeInstance = state.horde?.battlefield.find((i) => i.instanceId === instanceId);
      if (hordeInstance && state.horde) {
        const seedPower = hordeInstance.power ?? parsePrintedPTValue(hordeInstance.card.power ?? hordeInstance.card.cardFaces?.[0]?.power);
        const seedToughness = hordeInstance.toughness ?? parsePrintedPTValue(hordeInstance.card.toughness ?? hordeInstance.card.cardFaces?.[0]?.toughness);
        const updatedHordeBF = updateInstance(state.horde.battlefield, instanceId, (i) => ({
          ...i,
          power: seedPower + dPower,
          toughness: seedToughness + dToughness,
        })) as CardInstance[];
        updates.horde = { ...state.horde, battlefield: updatedHordeBF };
      }

      return updates;
    });
  },

  setPowerToughness: (instanceId: string, power: number, toughness: number) => {
    set((state) => {
      const zones: GameZone[] = ['battlefield', 'command'];
      const updates: Partial<PlaytesterState> = {};
      for (const zone of zones) {
        if (state[zone].some((i) => i.instanceId === instanceId)) {
          updates[zone] = updateInstance(state[zone], instanceId, (i) => ({
            ...i,
            power,
            toughness,
          })) as CardInstance[];
        }
      }
      // Also support horde battlefield
      if (state.horde?.battlefield.some((i) => i.instanceId === instanceId)) {
        const updatedHordeBF = updateInstance(state.horde.battlefield, instanceId, (i) => ({
          ...i,
          power,
          toughness,
        })) as CardInstance[];
        updates.horde = { ...state.horde, battlefield: updatedHordeBF };
      }
      return updates;
    });
  },

  resetPowerToughness: (instanceId: string) => {
    set((state) => {
      const zones: GameZone[] = ['battlefield', 'command'];
      const updates: Partial<PlaytesterState> = {};
      for (const zone of zones) {
        if (state[zone].some((i) => i.instanceId === instanceId)) {
          updates[zone] = updateInstance(state[zone], instanceId, (i) => {
            const { power: _p, toughness: _t, ...rest } = i;
            return rest as CardInstance;
          }) as CardInstance[];
        }
      }
      // Also support horde battlefield
      if (state.horde?.battlefield.some((i) => i.instanceId === instanceId)) {
        const updatedHordeBF = updateInstance(state.horde.battlefield, instanceId, (i) => {
          const { power: _p, toughness: _t, ...rest } = i;
          return rest as CardInstance;
        }) as CardInstance[];
        updates.horde = { ...state.horde, battlefield: updatedHordeBF };
      }
      return updates;
    });
  },

  // ── Player stats ───────────────────────────────────────────────────────────

  adjustLife: (delta: number) => {
    set((state) => {
      const next = state.lifeTotal + delta;
      const shouldLose = next <= 0 && state.horde !== null;
      return {
        lifeTotal: next,
        overlay: shouldLose ? 'lose' : state.overlay,
      };
    });
  },

  setLife: (value: number) => {
    set((state) => {
      const shouldLose = value <= 0 && state.horde !== null;
      return {
        lifeTotal: value,
        overlay: shouldLose ? 'lose' : state.overlay,
      };
    });
  },

  setTurnNumber: (value: number) => {
    set({ turnNumber: Math.max(1, value) });
  },

  nextTurn: () => {
    set((state) => {
      // Untap all non-doesntUntap permanents
      const battlefield = state.battlefield.map((i) =>
        i.doesntUntap ? i : { ...i, tapped: false }
      );

      // Draw 1
      const libArr = [...state.library];
      const drawn = libArr.shift();
      const hand = drawn ? [...state.hand, drawn] : [...state.hand];

      return {
        battlefield,
        library: libArr,
        hand,
        turnNumber: state.turnNumber + 1,
      };
    });
  },

  shuffleLibrary: () => {
    set((state) => ({
      library: shuffleInstances(state.library),
      diceResult: { label: 'Library', value: 'Shuffled' },
      diceResultExpiresAt: Date.now() + 2000,
    }));
  },

  drawCard: () => {
    set((state) => {
      if (state.library.length === 0) return {};
      const [drawn, ...remaining] = state.library;
      return {
        library: remaining,
        hand: drawn ? [...state.hand, drawn] : state.hand,
      };
    });
  },

  millTopX: (n: number) => {
    set((state) => {
      const milled = state.library.slice(0, n);
      const remaining = state.library.slice(n);
      return {
        library: remaining,
        graveyard: [...state.graveyard, ...milled],
      };
    });
  },

  adjustPlayerCounter: (type: keyof PlayerCounters, delta: number) => {
    set((state) => ({
      playerCounters: {
        ...state.playerCounters,
        [type]: state.playerCounters[type] + delta,
      },
    }));
  },

  // ── Commander ──────────────────────────────────────────────────────────────

  castCommander: (instanceId: string) => {
    set((state) => {
      const found = state.command.find((i) => i.instanceId === instanceId);
      if (!found) return {};
      const { zoom, battlefield } = state;
      const { cardW, cardH } = cardDimensions(zoom);
      const gap = 6; const cols = 8;
      const idx = battlefield.length;
      const updatedCard = {
        ...found,
        castCount: found.castCount + 1,
        x: found.x ?? (idx % cols) * (cardW + gap),
        y: found.y ?? Math.floor(idx / cols) * (cardH + gap),
      };
      return {
        command: removeFromZone(state.command, instanceId),
        battlefield: [...state.battlefield, updatedCard],
      };
    });
  },

  returnToCommandZone: (instanceId: string) => {
    set((state) => {
      const found = state.battlefield.find((i) => i.instanceId === instanceId);
      if (!found) return {};
      return {
        battlefield: removeFromZone(state.battlefield, instanceId),
        command: [...state.command, found],
      };
    });
  },

  // ── Token ──────────────────────────────────────────────────────────────────

  makeTokenCopy: (instanceId: string) => {
    set((state) => {
      // Locate the source card in whichever zone it currently occupies
      const zones: GameZone[] = ['battlefield', 'hand', 'graveyard', 'exile', 'command', 'library'];
      let source: CardInstance | undefined;
      for (const z of zones) {
        source = state[z].find((i) => i.instanceId === instanceId);
        if (source) break;
      }
      if (!source) return {};

      const { cardW } = cardDimensions(state.zoom);
      // Step proportional to card width so the ladder looks the same at any zoom
      const step = Math.round(cardW * 0.14);

      // Chain from the most recent token copy of this same card already on the
      // battlefield, so pressing X repeatedly walks down-right instead of
      // dropping every copy on the same spot. Falls back to the source card.
      const sameTokens = state.battlefield.filter(
        (i) => i.isToken && i.card.id === source!.card.id && i.x !== undefined,
      );
      const chainFrom = sameTokens[sameTokens.length - 1] ?? source;

      const baseX = chainFrom.x;
      const baseY = chainFrom.y;
      const x = baseX !== undefined ? Math.max(0, baseX + step) : undefined;
      const y = baseY !== undefined ? Math.max(0, baseY + step) : undefined;

      const { cardH } = cardDimensions(state.zoom);
      const gap = 6; const cols = 8;
      const idx = state.battlefield.length;
      const instance = {
        ...createInstance(source.card, false, true), // isToken: true
        x: x ?? (idx % cols) * (cardW + gap),
        y: y ?? Math.floor(idx / cols) * (cardH + gap),
      };
      return { battlefield: [...state.battlefield, instance] };
    });
  },

  placeToken: (card: Card, x?: number, y?: number) => {
    set((state) => {
      const { zoom, battlefield } = state;
      const { cardW, cardH } = cardDimensions(zoom);
      const gap = 6; const cols = 8;
      const idx = battlefield.length;
      const defaultX = (idx % cols) * (cardW + gap);
      const defaultY = Math.floor(idx / cols) * (cardH + gap);
      const instance = {
        ...createInstance(card, false, true),  // isToken: true
        x: x ?? defaultX,
        y: y ?? defaultY,
      };
      return { battlefield: [...state.battlefield, instance] };
    });
  },

  placeHordeToken: (card: Card, x?: number, y?: number) => {
    set((state) => {
      if (!state.horde) return {};
      const { zoom } = state;
      const { cardW, cardH } = cardDimensions(zoom);
      const gap = 6; const cols = 8;
      const idx = state.horde.battlefield.length;
      const defaultX = (idx % cols) * (cardW + gap);
      const defaultY = Math.floor(idx / cols) * (cardH + gap);
      const instance = {
        ...createInstance(card, false, true),  // isToken: true — ceases to exist when leaving
        x: x ?? defaultX,
        y: y ?? defaultY,
      };
      return {
        horde: {
          ...state.horde,
          battlefield: [...state.horde.battlefield, instance],
        },
      };
    });
  },

  setDeckTokens: (tokens: Card[]) => {
    set({ deckTokens: tokens });
  },

  setTokenSearchResults: (cards: Card[], query: string) => {
    set({ tokenSearchResults: cards, tokenSearchQuery: query });
  },

  // ── Scry ───────────────────────────────────────────────────────────────────

  openScry: (count: number) => {
    set((state) => {
      const scryCards = state.library.slice(0, count);
      const library = state.library.slice(count);
      return {
        scryCards,
        scryCount: count,
        scryBottom: new Set<string>(),
        library,
        openPanel: 'scry',
      };
    });
  },

  scryReorder: (fromIndex: number, toIndex: number) => {
    set((state) => {
      const cards = [...state.scryCards];
      const [moved] = cards.splice(fromIndex, 1);
      if (moved) cards.splice(toIndex, 0, moved);
      return { scryCards: cards };
    });
  },

  scryToggleBottom: (instanceId: string) => {
    set((state) => {
      const next = new Set(state.scryBottom);
      if (next.has(instanceId)) {
        next.delete(instanceId);
      } else {
        next.add(instanceId);
      }
      return { scryBottom: next };
    });
  },

  scryConfirm: () => {
    set((state) => {
      const kept = state.scryCards.filter((i) => !state.scryBottom.has(i.instanceId));
      const bottom = state.scryCards.filter((i) => state.scryBottom.has(i.instanceId));
      return {
        library: [...kept, ...state.library, ...bottom],
        scryCards: [],
        scryBottom: new Set<string>(),
        scryCount: 0,
        openPanel: null,
      };
    });
  },

  // ── Selection ──────────────────────────────────────────────────────────────

  setSelection: (ids: string[]) => {
    set({ selectedInstanceIds: new Set(ids) });
  },

  toggleSelection: (id: string) => {
    set((state) => {
      const next = new Set(state.selectedInstanceIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { selectedInstanceIds: next };
    });
  },

  clearSelection: () => {
    set({ selectedInstanceIds: new Set<string>() });
  },

  // ── UI ─────────────────────────────────────────────────────────────────────

  setZoom: (z: number) => {
    const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z / 10) * 10));
    persistZoom(clamped);
    set({ zoom: clamped });
  },

  adjustZoom: (delta: number) => {
    set((state) => {
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, state.zoom + delta));
      persistZoom(next);
      return { zoom: next };
    });
  },

  toggleClickToTap: () => {
    set((state) => ({ isClickToTapMode: !state.isClickToTapMode }));
  },

  toggleShowTopCard: () => {
    set((state) => ({ showTopCard: !state.showTopCard }));
  },

  setHoveredCard: (card: Card | null) => {
    set({ hoveredCard: card });
  },

  setHoveredInstanceId: (id: string | null) => {
    set({ hoveredInstanceId: id });
  },

  setHoveredZone: (zone: GameZone | null) => {
    set({ hoveredZone: zone });
  },

  setPersistentPreview: (card: Card | null) => {
    set({ persistentPreviewCard: card });
  },

  setOpenPanel: (p: PlaytesterPanel) => {
    set({ openPanel: p });
  },

  setMillInput: (n: number) => {
    set({ millInput: n });
  },

  setContextMenu: (m: { x: number; y: number; instanceId: string; zone: GameZone } | null) => {
    set({ contextMenu: m });
  },

  openCounterModal: () => {
    set({ showCounterModal: true });
  },

  closeCounterModal: () => {
    set({ showCounterModal: false });
  },

  openPTModal: () => {
    set({ showPTModal: true });
  },

  closePTModal: () => {
    set({ showPTModal: false });
  },

  rollDice: (type: 'coin' | 'd6' | '2d6' | 'd20') => {
    let result: { label: string; value: string };
    switch (type) {
      case 'coin':
        result = flipCoin();
        break;
      case 'd6':
        result = rollD6();
        break;
      case '2d6':
        result = roll2D6();
        break;
      case 'd20':
        result = rollD20();
        break;
      default:
        result = { label: 'Roll', value: '?' };
        break;
    }
    set({
      diceResult: result,
      diceResultExpiresAt: Date.now() + 3000,
    });
  },

  // ── Phase 2: Horde ─────────────────────────────────────────────────────────

  initHorde: (data: HordeDeckData, resolvedCards: Card[], size: 25 | 50 | 75 | 100) => {
    const state = get();

    // Build CardInstance list from resolved cards, shuffle, take first `size`
    const allInstances = resolvedCards.map((c) => createInstance(c));
    const shuffled = shuffleInstances(allInstances);
    const library = shuffled.slice(0, size);

    // Identify missing names (names in data that didn't come back from server)
    const resolvedNames = new Set(resolvedCards.map((c) => c.name.toLowerCase()));
    const missing = data.cards
      .filter((e) => !resolvedNames.has(e.name.toLowerCase()))
      .map((e) => e.name);

    const hordeState: HordeState = {
      deckId: data.id,
      deckName: data.name,
      deckSize: size,
      phase: 'setup',
      setupTurn: 1,
      library,
      battlefield: [],
      graveyard: [],
      revealPanel: [],
      millInput: 5,
      missingCards: missing,
    };

    // Reset player for horde: collect all cards from all zones (same pattern as restart()),
    // rebuild command zone + shuffled library, set life=30, draw 7.
    const allPlayerInstances = [
      ...state.library,
      ...state.hand,
      ...state.battlefield,
      ...state.graveyard,
      ...state.exile,
      ...state.command,
    ];
    const commandInstances = allPlayerInstances
      .filter((i) => i.isCommander)
      .map((i) => createInstance(i.card, true));
    const libraryInstances = shuffleInstances(
      allPlayerInstances
        .filter((i) => !i.isCommander && !i.isToken)
        .map((i) => createInstance(i.card, false)),
    );
    const { hand, library: playerLib } = drawFromLibrary(libraryInstances, [], 7);

    set({
      lifeTotal: 30,
      turnNumber: 1,
      horde: hordeState,
      overlay: null,
      command: commandInstances,
      library: playerLib,
      hand,
      battlefield: [],
      graveyard: [],
      exile: [],
    });
  },

  endSetupTurn: () => {
    set((state) => {
      if (!state.horde) return {};
      const nextSetup = state.horde.setupTurn + 1;

      // Untap all player permanents (respecting doesntUntap), draw 1, increment turnNumber
      const battlefield = state.battlefield.map((i) =>
        i.doesntUntap ? i : { ...i, tapped: false }
      );
      const libArr = [...state.library];
      const drawn = libArr.shift();
      const hand = drawn ? [...state.hand, drawn] : state.hand;
      const nextTurn = state.turnNumber + 1;

      const baseUpdate = {
        battlefield,
        hand,
        library: libArr,
        turnNumber: nextTurn,
      };

      // Three full setup turns: transition to player-turn only after clicking on turn 3
      // (nextSetup > 3 means the player has completed turn 3 and clicked End Setup)
      if (nextSetup > 3) {
        return { ...baseUpdate, horde: { ...state.horde, setupTurn: nextSetup, phase: 'player-turn' as const } };
      }
      return { ...baseUpdate, horde: { ...state.horde, setupTurn: nextSetup } };
    });
  },

  endPlayerTurn: () => {
    set((state) => {
      if (!state.horde) return {};
      const { revealed, remaining } = hordeReveal(state.horde.library);
      return {
        horde: {
          ...state.horde,
          library: remaining,
          revealPanel: revealed,
          phase: 'horde-resolving' as const,
        },
      };
    });
    // Check win condition after reveal (empty library + empty battlefield)
    get().checkHordeWinCondition();
  },

  beginMyTurn: () => {
    set((state) => {
      if (!state.horde) return {};
      // Untap all player permanents (respecting doesntUntap)
      const battlefield = state.battlefield.map((i) =>
        i.doesntUntap ? i : { ...i, tapped: false }
      );
      // Draw 1 card from player library
      const libArr = [...state.library];
      const drawn = libArr.shift();
      const hand = drawn ? [...state.hand, drawn] : [...state.hand];
      return {
        battlefield,
        library: libArr,
        hand,
        turnNumber: state.turnNumber + 1,
        horde: {
          ...state.horde,
          revealPanel: [],
          phase: 'player-turn' as const,
        },
      };
    });
  },

  hordeMillCards: (n: number) => {
    set((state) => {
      if (!state.horde) return {};
      const milled = state.horde.library.slice(0, n);
      const remaining = state.horde.library.slice(n);
      return {
        horde: {
          ...state.horde,
          library: remaining,
          graveyard: [...state.horde.graveyard, ...milled],
        },
      };
    });
    get().checkHordeWinCondition();
  },

  checkHordeWinCondition: () => {
    set((state) => {
      if (!state.horde) return {};
      const libraryEmpty = state.horde.library.length === 0 && state.horde.revealPanel.length === 0;
      const battlefieldEmpty = state.horde.battlefield.length === 0;
      if (libraryEmpty && battlefieldEmpty) {
        return { overlay: 'win' as const };
      }
      return {};
    });
  },

  dismissOverlay: (mode: 'continue' | 'return' | 'new-game') => {
    if (mode === 'continue') {
      set({ overlay: null });
    } else if (mode === 'return') {
      set({ overlay: null });
      get().exitGame();
    } else if (mode === 'new-game') {
      // Same behaviour as pressing Restart during a horde game — one shared
      // implementation so the two can never drift apart.
      const hordeRestart = buildHordeRestart(get());
      set(hordeRestart ?? { overlay: null });
    }
  },

  moveHordeCardToZone: (instanceId: string, fromZone: HordeZone, toZone: HordeZone) => {
    set((state) => {
      if (!state.horde) return {};
      const fromArr = [...state.horde[fromZone]];
      const idx = fromArr.findIndex((i) => i.instanceId === instanceId);
      if (idx === -1) return {};
      const spliced = fromArr.splice(idx, 1);
      const moved = spliced[0];
      if (!moved) return {};

      // Magic rule 111.7: player-created tokens (isToken:true) cease to exist
      // the moment they leave the horde battlefield.
      if (fromZone === 'battlefield' && moved.isToken) {
        return {
          horde: {
            ...state.horde,
            [fromZone]: fromArr,
          },
        };
      }

      const toArr = [...state.horde[toZone], moved];
      return {
        horde: {
          ...state.horde,
          [fromZone]: fromArr,
          [toZone]: toArr,
        },
      };
    });
    // After any battlefield removal, check win condition
    get().checkHordeWinCondition();
  },

  setHordeMoveResult: (instanceId: string, toZone: HordeZone) => {
    set((state) => {
      if (!state.horde) return {};
      const panel = [...state.horde.revealPanel];
      const idx = panel.findIndex((i) => i.instanceId === instanceId);
      if (idx === -1) return {};
      const [moved] = panel.splice(idx, 1);
      const toArr = [...state.horde[toZone], moved];
      return {
        horde: {
          ...state.horde,
          revealPanel: panel,
          [toZone]: toArr,
        },
      };
    });
    get().checkHordeWinCondition();
  },

  setHordeMillInput: (n: number) => {
    set((state) => {
      if (!state.horde) return {};
      return { horde: { ...state.horde, millInput: n } };
    });
  },
}));
